import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { IPerson, TPersonValidationErrors } from "interfaces/interfaces";
import { UserAPI } from "API/userApi";

import { useContextHospi } from "context/ContextHospi";
import {
  HOST_PROFILE_EDIT_ROUTE,
  HOST_PROFILE_VIEW_ROUTE,
  LOGIN_ROUTE
} from "utils/constants";

import { generateErrorResponse } from "utils/helpers";
import { WizardStepPersonalInfoView } from "../../../components/Wizard/Student/Steps/WizardStepPersonalInfo";
import { WizardStepAvatarView } from "../../../components/Wizard/Student/Steps/WizardStepAvatar";
import { Button } from "antd";
import {
  getChangedFields,
  getStepErrorsFromResponse
} from "../../../components/Wizard/utils";
import { isEmpty } from "lodash";
import {
  Content,
  Footer,
  FooterButtons,
  Header,
  PageTitle,
  Root,
  SectionContainer,
  Sections
} from "../StudentProfileEdit/styles";
import { useHospiNavigate } from "../../../utils/common";
import {
  T_WIZARD_HOST_STEP,
  WIZARD_HOST_STEPS,
  WIZARD_STUDENT_STEPS
} from "../../../components/Wizard/interfaces";
import * as H from "history";
import { WizardStepReferralView } from "../../../components/Wizard/Host/Steps/WizardStepReferral";
import { SectionHeaderComponent } from "../StudentProfileEdit";
import { scrollToFormWithError } from "../../../utils/dom";
import { UserProfilePasswordSection } from "../UserProfilePasswordSection";
import { UserProfileEmailPreferencesSection } from "../UserProfileEmailPreferencesSection";
import { useConfirmModal } from "../../../context/ContextConfirmModal";
import { useNotifier } from "../../../context/ContextNotifier";
import { VerifiedInfo } from "../SimpleComponents";

export type TAdditionalSections = "EmailPreferences";

type TAllSections = T_WIZARD_HOST_STEP | TAdditionalSections;

export const HostProfileEdit: React.FC<{
  viewonly?: boolean;
  canCancel?: boolean;
  canDelete?: boolean;
}> = ({ viewonly = false, canCancel = false, canDelete = false }) => {
  const { t, i18n } = useTranslation("student");
  const { t: th } = useTranslation("host");
  const navigate = useHospiNavigate();
  const location: H.Location<{ stepName?: TAllSections } | null> =
    useLocation();
  const [errors, setErrors] = useState<TPersonValidationErrors>({});

  const { userProfile, updateProfile, currentUserId, logOut } =
    useContextHospi();

  const [isLoading, setIsLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const stepsRefs = useRef<{
    [key in TAllSections]?: HTMLDivElement | null;
  }>({});
  const confirmModal = useConfirmModal();
  const notifier = useNotifier();

  const readonly = isLoading;

  const [editedProfile, setEditedProfile] = useState<IPerson | null>(null);

  useEffect(() => {
    if (userProfile) {
      setEditedProfile(userProfile);
    }
  }, [userProfile]);

  if (!localStorage.getItem("student_account_confirmation_executed")) {
    // Add the code block to the dataLayer
    window.dataLayer.push({
      event: "account_confirmation_student",
      user_id: currentUserId,
      user_type: "student"
    });

    // Set the flag in localStorage to indicate that the code has been executed
    localStorage.setItem("student_account_confirmation_executed", "true");
  }

  useEffect(() => {
    if (
      userProfile?.not_provided_fields &&
      userProfile?.not_provided_fields?.length > 0
    ) {
      const newErrors: TPersonValidationErrors = {};

      userProfile?.not_provided_fields?.forEach((f) => {
        newErrors[f as keyof IPerson] = "Required";
      });

      setErrors(newErrors);
    }
  }, [userProfile]);

  const sendFromData = async () => {
    if (!editedProfile) {
      return;
    }

    setIsLoading(true);
    const userFormData: Partial<IPerson> = {
      user_id: currentUserId,
      first_name: editedProfile.first_name,
      last_name: editedProfile.last_name,
      phone_code: editedProfile.phone_code,
      phone_number: editedProfile.phone_number,
      gender: editedProfile.gender,
      spoken_languages: editedProfile.spoken_languages,
      default_language: i18n.language,
      description: editedProfile.description,
      privacy: editedProfile.privacy,
      user_type: editedProfile.user_type,
      birth_date: editedProfile.birth_date,
      birth_country: editedProfile.birth_country,
      source_to_hospi: editedProfile.source_to_hospi,
      reason_to_become_host: editedProfile.reason_to_become_host,
      email_preferences_chat: editedProfile.email_preferences_chat,
      email_preferences_general: editedProfile.email_preferences_general,
      email_preferences_room: editedProfile.email_preferences_room,
      email_preferences_match: editedProfile.email_preferences_match
    };
    try {
      const changedFields = getChangedFields(userProfile!, userFormData);
      let updatedUser: IPerson | undefined;
      if (!isEmpty(changedFields)) {
        updatedUser = await UserAPI.updateHost(
          userProfile?.user_id!,
          getChangedFields(userProfile!, userFormData)
        );
      }
      if (avatarFile) {
        const { thumbnail_url } = await UserAPI.uploadAvatar(
          currentUserId!,
          avatarFile,
          {
            showNotificationOnError: false
          }
        );
        updatedUser = updatedUser
          ? { ...updatedUser, thumbnail_url }
          : { ...editedProfile, thumbnail_url };
      }
      if (updatedUser) {
        updateProfile(updatedUser);
      }
      setIsLoading(false);
      if (!updatedUser || updatedUser?.not_provided_fields?.length === 0) {
        navigate(HOST_PROFILE_VIEW_ROUTE);
      }
    } catch (e) {
      setIsLoading(false);
      const errorResponse = generateErrorResponse(e);
      if (errorResponse.hasOwnProperty("status")) {
        if (errorResponse.status === 401) {
          navigate(LOGIN_ROUTE);
        }
        setErrors(getStepErrorsFromResponse(e) as TPersonValidationErrors);

        scrollToFormWithError(rootRef.current!);
      }
    }
    window.dataLayer.push({
      event: "account_finished_student",
      user_id: currentUserId,
      user_type: "student"
    });
  };

  const handleEdit = (
    _: React.MouseEvent<HTMLButtonElement>,
    stepName: TAllSections
  ) => {
    navigate(HOST_PROFILE_EDIT_ROUTE, {
      state: {
        stepName: stepName,
        noNeedScrollTop: true
      }
    });
  };

  useEffect(() => {
    if (location.state?.stepName) {
      setTimeout(() => {
        if (
          location.state?.stepName &&
          stepsRefs.current[location.state.stepName]
        ) {
          stepsRefs.current[location.state.stepName]?.scrollIntoView();
        }
      }, 500);
    }
  }, [location.state?.stepName]);

  return (
    <Root ref={rootRef}>
      {editedProfile ? (
        <>
          <Content>
            <Header>
              <PageTitle>{th("viewTitle")}</PageTitle>
              <VerifiedInfo />
            </Header>
            <Sections $isProfileViewOnly={viewonly}>
              <SectionContainer
                ref={(el) => {
                  stepsRefs.current[WIZARD_HOST_STEPS.Avatar] = el;
                }}
              >
                <SectionHeaderComponent
                  stepName={WIZARD_HOST_STEPS.Avatar}
                  onEdit={handleEdit}
                  showEditButton={viewonly}
                  translationNamespace="host"
                />
                <WizardStepAvatarView
                  avatarThumbnailUrl={userProfile?.thumbnail_url}
                  errors={null}
                  onFileChange={(file) => {
                    setAvatarFile(file);
                  }}
                  readonly={readonly}
                  viewonly={viewonly}
                />
              </SectionContainer>
              <SectionContainer
                ref={(el) => {
                  stepsRefs.current[WIZARD_STUDENT_STEPS.PersonalInfo] = el;
                }}
              >
                <SectionHeaderComponent
                  stepName={WIZARD_HOST_STEPS.PersonalInfo}
                  onEdit={handleEdit}
                  showEditButton={viewonly}
                  translationNamespace="host"
                />
                <WizardStepPersonalInfoView
                  values={editedProfile}
                  errors={errors}
                  setValues={(values) => {
                    setEditedProfile(
                      (prev) =>
                        ({
                          ...prev,
                          ...values
                        } as IPerson)
                    );
                  }}
                  readonly={readonly}
                  viewonly={viewonly}
                />
              </SectionContainer>
              <SectionContainer
                ref={(el) => {
                  stepsRefs.current[WIZARD_HOST_STEPS.Referral] = el;
                }}
              >
                <SectionHeaderComponent
                  stepName={WIZARD_HOST_STEPS.Referral}
                  onEdit={handleEdit}
                  showEditButton={viewonly}
                  translationNamespace="host"
                />
                <WizardStepReferralView
                  values={editedProfile}
                  errors={errors}
                  setValues={(values) => {
                    setEditedProfile(
                      (prev) =>
                        ({
                          ...prev,
                          ...values
                        } as IPerson)
                    );
                  }}
                  readonly={readonly}
                  viewonly={viewonly}
                />
              </SectionContainer>
              <UserProfilePasswordSection />
              <SectionContainer
                ref={(el) => {
                  stepsRefs.current["EmailPreferences"] = el;
                }}
              >
                <SectionHeaderComponent<TAllSections>
                  stepName={"EmailPreferences"}
                  title={t(`email_preferences.title`)}
                  onEdit={handleEdit}
                  showEditButton={viewonly}
                  translationNamespace="host"
                />
                <UserProfileEmailPreferencesSection
                  values={{
                    email_preferences_match:
                      editedProfile.email_preferences_match,
                    email_preferences_general:
                      editedProfile.email_preferences_general,
                    email_preferences_chat:
                      editedProfile.email_preferences_chat,
                    email_preferences_room: editedProfile.email_preferences_room
                  }}
                  onChange={(values) => {
                    setEditedProfile((prev) => {
                      if (!prev) {
                        return null;
                      }

                      return {
                        ...prev,
                        ...values
                      };
                    });
                  }}
                  readonly={viewonly || readonly}
                />
              </SectionContainer>
            </Sections>
            <Footer>
              <FooterButtons>
                {canDelete ? (
                  <Button
                    danger
                    size="large"
                    onClick={() => {
                      confirmModal.show({
                        title: t("studentProfile.deleteAccount.title"),
                        content: t("studentProfile.deleteAccount.text"),
                        onConfirm: async function () {
                          await UserAPI.delete(userProfile?.user_id!);
                          notifier.success({
                            message: t(
                              "studentProfile.confirmDeleteModal.deleted"
                            )
                          });
                          logOut();
                        },
                        confirmButtonText: t(
                          "studentProfile.deleteAccount.buttonDelete"
                        ),
                        confirmButtonProps: { danger: true },
                        cancelButtonText: t(
                          "studentProfile.deleteAccount.buttonKeep"
                        )
                      });
                    }}
                  >
                    {t("studentProfile.buttonRemove.title")}
                  </Button>
                ) : null}
                <div
                  style={{
                    flexGrow: 1
                  }}
                />
                {!viewonly ? (
                  <>
                    {canCancel ? (
                      <Button
                        size="large"
                        onClick={() => {
                          navigate(HOST_PROFILE_VIEW_ROUTE);
                        }}
                      >
                        {t("studentProfile.buttonCancel.title")}
                      </Button>
                    ) : null}
                    <Button
                      size="large"
                      onClick={() => {
                        sendFromData();
                      }}
                      type={"primary"}
                      loading={isLoading}
                    >
                      {t("studentProfile.buttonSave.title")}
                    </Button>
                  </>
                ) : null}
              </FooterButtons>
            </Footer>
          </Content>
        </>
      ) : null}
    </Root>
  );
};
